/**
 * @file ViewportProbe.ts
 * @role Browser measurement seam for the actually visible viewport, safe-area insets, and DPR.
 * @owns ViewportMeasurement, ViewportProbe, BrowserViewportProbe.
 * @reads visualViewport with window-size fallbacks and an injected safe-area provider.
 * @neverDoes Classify layouts, position UI, or touch Pixi.
 */
import { ZERO_SAFE_AREA, type SafeArea } from './SafeArea';

export interface ViewportMeasurement {
  width: number;
  height: number;
  devicePixelRatio: number;
  safeArea: SafeArea;
}

export interface ViewportProbe {
  measure(): ViewportMeasurement;
}

export class BrowserViewportProbe implements ViewportProbe {
  constructor(private readonly safeAreaProvider: () => SafeArea = () => ({ ...ZERO_SAFE_AREA })) {}

  measure(): ViewportMeasurement {
    if (typeof window === 'undefined') {
      return { width: 1, height: 1, devicePixelRatio: 1, safeArea: this.safeAreaProvider() };
    }

    const visual = window.visualViewport;
    return {
      width: positiveFinite(visual?.width, window.innerWidth || 1),
      height: positiveFinite(visual?.height, window.innerHeight || 1),
      devicePixelRatio: positiveFinite(window.devicePixelRatio, 1),
      safeArea: this.safeAreaProvider(),
    };
  }
}

function positiveFinite(value: number | undefined, fallback: number): number {
  return value !== undefined && Number.isFinite(value) && value > 0 ? value : fallback;
}
